import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { RecruitingGate, isRecruitingRequired } from "@/components/Pricing";
import Header from "@/components/Header";
import MatchScoreRing from "@/components/MatchScoreRing";
import { Search, Mail, GitBranch, X } from "lucide-react";
import StatusBadges from "@/components/StatusBadges";
import { toast } from "sonner";

export default function TalentPool() {
  const [coaches, setCoaches] = useState([]);
  const [q, setQ] = useState("");
  const [filterGender, setFilterGender] = useState("");
  const [filterLevel, setFilterLevel] = useState("");
  const [filterPosition, setFilterPosition] = useState("");
  const [loading, setLoading] = useState(true);
  const [gated, setGated] = useState(false);
  const [inviting, setInviting] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const learnedUnderId = searchParams.get("learned_under") || "";
  const [learnedUnderName, setLearnedUnderName] = useState("");
  const [topMentors, setTopMentors] = useState([]);

  useEffect(() => {
    // Populate the "By Mentor" dropdown with the Top 10 elite mentors from the DB.
    api.get("/coaches/top-mentors?limit=10")
      .then(({ data }) => setTopMentors(Array.isArray(data) ? data : []))
      .catch(() => setTopMentors([]));
  }, []);

  const applyMentorFilter = (mentorId) => {
    if (mentorId) {
      searchParams.set("learned_under", mentorId);
    } else {
      searchParams.delete("learned_under");
    }
    setSearchParams(searchParams);
  };

  // Position chips — HC=Head Coach, AC=Assistant, OC=Offensive Coordinator,
  // DC=Defensive Coordinator, FO=Faceoff Coach, Goalie=Goalie Coach.
  const POSITIONS = [
    { id: "HC", label: "HC", full: "Head Coach" },
    { id: "AC", label: "AC", full: "Assistant Coach" },
    { id: "OC", label: "OC", full: "Offensive Coordinator" },
    { id: "DC", label: "DC", full: "Defensive Coordinator" },
    { id: "FO", label: "FO", full: "Faceoff" },
    { id: "Goalie", label: "GOALIE", full: "Goalie" },
  ];

  const invite = async (e, coachId) => {
    e.preventDefault();
    e.stopPropagation();
    setInviting(coachId);
    try {
      await api.post("/claim-invites", { coach_profile_id: coachId });
      toast.success("Claim invite created — see Invites tab");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create invite");
    } finally { setInviting(null); }
  };

  useEffect(() => {
    const params = learnedUnderId ? `?learned_under=${learnedUnderId}` : "";
    api.get(`/coaches${params}`).then(({ data }) => { setCoaches(data); setLoading(false); }).catch((e) => { if (isRecruitingRequired(e)) setGated(true); setLoading(false); });
    // Fetch mentor name for the pill label
    if (learnedUnderId) {
      api.get(`/coaches/${learnedUnderId}`).then(({ data }) => {
        setLearnedUnderName(data?.display_name || data?.name || "");
      }).catch(() => setLearnedUnderName(""));
    } else {
      setLearnedUnderName("");
    }
  }, [learnedUnderId]);

  const clearLearnedUnder = () => {
    searchParams.delete("learned_under");
    setSearchParams(searchParams);
  };

  // Position match: walk every signal — claimed coach's `desired_roles` /
  // `desired_role`, current/most-recent stint role + assistant_position,
  // the top-level `current_role` field set by the bio enricher, AND the
  // coach's self-declared `specialties[]` (so a coach who tagged both
  // "Offensive Coordinator" + "Goalie Coach" surfaces in BOTH filters).
  const positionMatches = (coach, pos) => {
    if (!pos) return true;
    const tokens = [
      coach.current_role,
      coach.desired_role,
      ...(coach.desired_roles || []),
      ...(coach.specialties || []),
      ...(coach.coaching_history || []).flatMap((s) => [s.role, s.assistant_position]),
    ]
      .filter(Boolean)
      .map((t) => String(t).toLowerCase());
    const blob = tokens.join(" | ");
    if (pos === "HC") return /\bhead coach\b/.test(blob);
    if (pos === "AC") return /\bassistant\b/.test(blob) && !/coordinator/.test(blob);
    if (pos === "OC") return /(offensive coordinator|\boc\b|\boffense\b)/.test(blob);
    if (pos === "DC") return /(defensive coordinator|\bdc\b|\bdefense\b)/.test(blob);
    if (pos === "FO") return /(face-?off|\bfogo\b|\bface\b)/.test(blob);
    if (pos === "Goalie") return /(goalie|goalkeeper|\bgk\b|goaltend)/.test(blob);
    return true;
  };

  // Defensive level match — canonicalize both sides to handle legacy DB
  // values like "High School" that should read as "HS".
  const _normLevel = (lvl) => {
    const s = String(lvl || "").trim();
    if (!s) return "";
    if (/^high\s*school$/i.test(s)) return "HS";
    if (/^junior\s*college$/i.test(s)) return "JUCO";
    if (/^middle\s*school$/i.test(s)) return "MS";
    return s;
  };

  const filtered = coaches.filter((c) => {
    if (filterGender && c.gender !== filterGender) return false;
    if (filterLevel) {
      const lvl = _normLevel(c.primary_level);
      const matchesPrimary = lvl === filterLevel;
      // Cross-level willingness: college coach with open_to_hs_jobs=true
      // shows up when AD filters for HS. Same in reverse for HS→college.
      const matchesOptIn = (
        (filterLevel === "HS" && c.open_to_hs_jobs && (lvl === "College" || lvl === "College Club")) ||
        ((filterLevel === "College" || filterLevel === "College Club") && c.open_to_college_jobs && (lvl === "HS" || lvl === "MS"))
      );
      if (!matchesPrimary && !matchesOptIn) return false;
    }
    if (!positionMatches(c, filterPosition)) return false;
    if (q && !(c.name + " " + (c.headline || "")).toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  });

  return (
    <div className="min-h-screen bg-appbg text-white">
      <Header />
      <div className="px-4 md:px-8 lg:px-12 py-8" data-testid="talent-pool-page">
        <div className="text-xs tracking-widest text-blaze font-bold">HEADHUNTERAGENT POOL</div>
        <h1 className="heading text-4xl md:text-5xl mt-2">TALENT <span className="text-blaze">POOL</span></h1>
        <p className="text-neutral-200 mt-2 max-w-xl">Claimed + scouted coaches ranked by <span className="text-volt font-bold">Coach CIR</span>. Click any to open the full dossier.</p>

        {learnedUnderId && (
          <div
            className="mt-4 flex items-center gap-3 bg-blaze/10 border-l-4 border-blaze px-4 py-3"
            data-testid="learned-under-pill"
          >
            <GitBranch className="text-blaze shrink-0" size={20} />
            <div className="min-w-0 flex-1">
              <div className="text-[10px] tracking-widest text-blaze font-bold">COACHING-TREE FILTER</div>
              <div className="text-sm text-white">
                Showing every coach who ever <b>learned under {learnedUnderName || "this coach"}</b>
              </div>
            </div>
            <button
              onClick={clearLearnedUnder}
              className="flex items-center gap-1 text-[10px] tracking-widest text-white hover:text-blaze border border-neutral-600 hover:border-blaze px-3 py-1.5"
              data-testid="clear-learned-under"
            >
              <X size={12} /> CLEAR
            </button>
          </div>
        )}

        <div className="mt-6 flex flex-wrap gap-2 items-center border-l-4 border-blaze border-y border-r border-neutral-700 p-3 bg-appcard">
          <Search className="text-blaze" size={18} />
          <input className="bg-transparent flex-1 px-2 outline-none text-sm placeholder:text-neutral-400" data-testid="talent-search-input" placeholder="Search by name or headline…" value={q} onChange={(e) => setQ(e.target.value)} />
          <select className="bg-appbg border-2 border-volt/60 text-volt px-2 py-1.5 text-xs" data-testid="filter-gender" value={filterGender} onChange={(e) => setFilterGender(e.target.value)}>
            <option value="">All genders</option>
            <option>Men</option><option>Women</option><option>Coed</option>
          </select>
          <select className="bg-appbg border-2 border-volt/60 text-volt px-2 py-1.5 text-xs" data-testid="filter-level" value={filterLevel} onChange={(e) => setFilterLevel(e.target.value)}>
            <option value="">All levels</option>
            <option>MS</option><option>HS</option><option>JUCO</option><option>College Club</option><option>College</option>
          </select>
          <select
            className="bg-appbg border-2 border-blaze/70 text-blaze px-2 py-1.5 text-xs font-bold max-w-[220px]"
            data-testid="filter-by-mentor"
            value={learnedUnderId}
            onChange={(e) => applyMentorFilter(e.target.value)}
            title="Show every coach in the Talent Pool who worked under this mentor"
          >
            <option value="">🌳 By Mentor · (all)</option>
            {topMentors.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name} · {m.disciple_count}👥 · {m.championships}🏆
              </option>
            ))}
          </select>
        </div>

        {/* Position chips — HC / AC / OC / DC / FO / Goalie */}
        <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="position-filter-row">
          <span className="text-[10px] tracking-widest text-blaze font-bold">POSITION:</span>
          <button
            onClick={() => setFilterPosition("")}
            data-testid="position-filter-all"
            className={`text-[10px] tracking-widest px-3 py-1.5 border-2 font-bold transition-colors ${
              filterPosition === "" ? "border-volt text-white bg-volt" : "border-volt/40 text-volt hover:bg-volt/20"
            }`}
          >ALL</button>
          {POSITIONS.map((p) => (
            <button
              key={p.id}
              onClick={() => setFilterPosition(p.id)}
              title={p.full}
              data-testid={`position-filter-${p.id}`}
              className={`text-[10px] tracking-widest px-3 py-1.5 border-2 font-bold transition-colors ${
                filterPosition === p.id ? "border-volt text-white bg-volt" : "border-volt/40 text-volt hover:bg-volt/20"
              }`}
            >{p.label}</button>
          ))}
          <span className="text-[10px] text-white font-bold ml-auto bg-blaze px-2 py-1 tracking-widest">{filtered.length} <span className="opacity-70">of {coaches.length}</span></span>
        </div>

        {gated && <div className="mt-8"><RecruitingGate feature="Proactive talent search" /></div>}
        {loading ? <div className="mt-8 heading text-2xl">LOADING…</div> : gated ? null : (
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            {filtered.map((c) => (
              <div
                key={c.id}
                data-testid={`talent-card-${c.id}`}
                className={`bg-appcard p-5 border-l-4 border border-neutral-700 transition-all hover:border-volt hover:shadow-[0_0_0_1px_rgb(var(--app-accent-rgb))] ${
                  c.claimed ? "border-l-volt" : "border-l-blaze"
                }`}
              >
                <Link to={`/coaches/${c.id}`} className="flex items-start justify-between gap-3 block">
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-neutral-500 tracking-widest">{c.gender?.toUpperCase()} · {c.sport?.toUpperCase()} · {c.primary_level}</div>
                    <div className="heading text-xl mt-1 truncate">{c.display_name || c.name}</div>
                    {!c.claimed && <div className="text-blaze text-[10px] tracking-widest mt-1">SCOUTED · UNCLAIMED</div>}
                    <StatusBadges
                      className="mt-1"
                      coachPro={c.status_badges?.coach_pro}
                      foundingMember={c.status_badges?.founding_member}
                    />

                    {c.headline && <div className="text-neutral-400 text-sm mt-1 line-clamp-2">{c.headline}</div>}
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {(c.desired_divisions || []).slice(0, 4).map((d) => (
                        <span key={d} className="text-[10px] tracking-widest border border-neutral-800 px-2 py-0.5 text-neutral-300">{d}</span>
                      ))}
                    </div>
                  </div>
                  <div className="flex flex-col items-center gap-1 shrink-0">
                    <MatchScoreRing score={c.qbr?.score || 0} label="CIR" size={75} stroke={6} />
                    {c.qbr?.best_target_level && (
                      <span className="text-[9px] tracking-widest text-volt font-bold" data-testid={`best-fit-${c.id}`}>
                        BEST FIT · {c.qbr.best_target_level}
                      </span>
                    )}
                  </div>
                </Link>
                {!c.claimed && (
                  <button
                    onClick={(e) => invite(e, c.id)}
                    disabled={inviting === c.id}
                    data-testid={`invite-to-claim-${c.id}`}
                    className="mt-3 inline-flex items-center gap-2 min-h-[40px] px-3 border border-blaze/40 text-blaze text-xs tracking-widest hover:bg-blaze hover:text-white transition-colors disabled:opacity-50"
                  >
                    <Mail size={12} /> {inviting === c.id ? "CREATING…" : "INVITE TO CLAIM"}
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
